import { Link, usePathname } from "expo-router";
import { type ReactNode } from "react";
import { Image, Platform, Pressable, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { UserIcon } from "@/design/icons";
import { useAuthState } from "@/auth/AuthProvider";

import { getAppHeaderVerticalPadding } from "@/design/headerMetrics";
import { useAppRouter } from "@/lib/appRouter";
import { webControlHoverTransition } from "@/design/interactionMotion";
import { hintWebNavigationDirection } from "@/design/navigationMotionState";
import { Text } from "@/design/Text";
import { fonts, hoverRingStrong, palette, space } from "@/design/tokens";

/** Horizontal rhythm follows the page gutter. Vertical rhythm guarantees a
 * designed minimum on compact devices and echoes large safe-area insets below
 * the controls, so the header stays optically framed on every platform.
 */
const HEADER_PAD = Platform.select({ web: space.xl, default: space.lg }) ?? space.lg;
const BACK_TOUCH_INSET = { bottom: 12, left: 12, right: 12, top: 12 } as const;

/** The real brand mark. */
function BrandMark() {
  return (
    <Image
      source={require("../../assets/brand-icon.png")}
      style={styles.brandMark}
      accessibilityLabel="Recital Master"
    />
  );
}

/**
 * Top bar: back affordance or brand left, account (or a page-specific pill)
 * right. Fixed control height so it never shifts between pages.
 */
export function AppHeader({
  back = false,
  backHref,
  backLabel,
  onBackPress,
  right
}: {
  back?: boolean;
  /**
   * Where Back lands when a refreshed tab has no stack to pop: the screen's
   * logical parent — the destination the back label promises. Defaults to
   * the recitals home.
   */
  backHref?: string;
  backLabel?: string;
  onBackPress?: () => void;
  right?: ReactNode;
}) {
  const router = useAppRouter();
  const pathname = usePathname();
  const insets = useSafeAreaInsets();
  const verticalPadding = getAppHeaderVerticalPadding(insets.top);
  const accountActive = pathname === "/account" || pathname.startsWith("/account/");
  const { demoMode } = useAuthState();

  return (
    <View
      style={[
        styles.row,
        verticalPadding,
        { pointerEvents: Platform.OS === "web" ? "none" : "box-none" }
      ]}
    >
      <View style={styles.controlTarget}>
        {back ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Back"
            hitSlop={BACK_TOUCH_INSET}
            pressRetentionOffset={BACK_TOUCH_INSET}
            onPress={() => {
              if (onBackPress) {
                onBackPress();
                return;
              }
              if (router.canGoBack()) {
                // On web, a real history traversal goes through the rise-layer
                // popstate controller. Re-dispatching router.back() after its
                // prevented exit can refocus the parked screen without
                // restarting its reveal, leaving the destination at opacity 0.
                if (Platform.OS === "web" && typeof window !== "undefined") {
                  window.history.back();
                  return;
                }
                router.back();
                return;
              }
              hintWebNavigationDirection(-1);
              router.replace((backHref ?? "/recitals") as never);
            }}
            style={({ pressed }) => [styles.back, pressed && styles.backPressed]}
          >
            <Text color={palette.textSoft} style={styles.backGlyph}>←</Text>
            <Text variant="smallStrong" color={palette.textSoft} numberOfLines={1}>
              {backLabel ?? "Back"}
            </Text>
          </Pressable>
        ) : (
          <Link href="/recitals" accessibilityLabel="Recital Master home">
            <View style={styles.brandRow}>
              <BrandMark />
              <Text style={styles.wordmark}>Recital Master</Text>
            </View>
          </Link>
        )}
      </View>
      <View style={styles.controlTarget}>
        {right ?? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={demoMode ? "Demo mode account" : "My account"}
            accessibilityState={{ disabled: accountActive }}
            disabled={accountActive}
            onPress={() => {
              if (!accountActive) router.push("/account");
            }}
            style={({ hovered, focused, pressed }) => [
              styles.accountChip,
              (hovered || focused) && styles.accountChipLit,
              pressed && styles.accountChipPressed
            ]}
          >
            <UserIcon size={15} color={palette.textSoft} />
            <Text variant="smallStrong" color={palette.textSoft}>
              {demoMode ? "Demo mode" : "My account"}
            </Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    minHeight: 34,
    // The header owns all of its padding, so wrappers only position it.
    paddingHorizontal: HEADER_PAD
  },
  brandRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.sm + 2
  },
  brandMark: {
    height: 24,
    width: 24
  },
  controlTarget: {
    pointerEvents: "auto"
  },
  wordmark: {
    color: palette.text,
    fontFamily: fonts.display,
    fontSize: 17,
    letterSpacing: -0.3
  },
  back: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.sm,
    paddingVertical: space.xs
  },
  // iOS bar-button idiom: a bare text control has no surface to darken, so
  // the dim must be strong enough to read on the label itself.
  backPressed: {
    opacity: 0.5
  },
  backGlyph: {
    fontSize: 16
  },
  accountChip: {
    alignItems: "center",
    backgroundColor: palette.coolWash,
    borderColor: "transparent",
    borderRadius: 999,
    borderWidth: 1,
    flexDirection: "row",
    gap: 7,
    paddingHorizontal: space.md,
    paddingVertical: 6,
    ...webControlHoverTransition
  },
  accountChipLit: {
    backgroundColor: palette.coolWashHover,
    ...hoverRingStrong
  },
  // Wash deepen + dim so touch (no hover state) still gets both halves of
  // the response; deliberately no ring — that's a pointer/keyboard affordance.
  accountChipPressed: {
    backgroundColor: palette.coolWashHover,
    opacity: 0.85
  }
});
